import LegalPageLayout, {
  LegalH2,
  LegalH3,
  LegalTable,
  Th,
  Td,
  LegalLink,
} from '@/components/LegalPageLayout';

const PrivacyPolicy = () => (
  <LegalPageLayout
    title="Privacy Policy"
    effectiveDate="06 April 2026"
    lastUpdated="06 April 2026"
    version="1.0"
  >
    {/* ── 1. Introduction ──────────────────────────────────────────────── */}
    <LegalH2>1. Introduction</LegalH2>
    <p>
      Share Shack ("we", "our", "us") is a private, invite-only item-sharing and
      loan-tracking application. It enables users within trusted groups (friends,
      families, communities) to catalogue items they own, share visibility of
      those items with group members, and record who has borrowed what.
    </p>
    <p style={{ marginTop: 12 }}>
      This Privacy Policy explains how we collect, use, store, share, and protect
      your personal data when you use the Share Shack application and website
      (collectively, the "Service"). It applies to all users and visitors.
    </p>
    <p style={{ marginTop: 12 }}>
      We are committed to protecting your privacy in accordance with the UK
      General Data Protection Regulation (UK GDPR), the Data Protection Act 2018
      (DPA 2018), and the ICO's Age Appropriate Design Code (Children's Code).
    </p>

    {/* ── 2. Data Controller ───────────────────────────────────────────── */}
    <LegalH2>2. Data Controller</LegalH2>
    <p>
      The data controller responsible for your personal data is:
    </p>
    <p style={{ marginTop: 8 }}>
      <strong>Gavin Davies</strong>
      <br />
      Email:{' '}
      <LegalLink href="mailto:support@shareshack.co.uk">
        support@shareshack.co.uk
      </LegalLink>
    </p>
    <p style={{ marginTop: 8 }}>
      You can contact us at any time regarding your personal data using the
      details above.
    </p>

    {/* ── 3. What Personal Data We Collect ──────────────────────────────── */}
    <LegalH2>3. What Personal Data We Collect</LegalH2>
    <p>
      We collect only the minimum data necessary for the Service to function. We
      do not collect date of birth, postal address, postcode, precise location,
      phone number, or financial payment details directly.
    </p>

    <LegalH3>3.1 Account Data</LegalH3>
    <LegalTable>
      <thead>
        <tr>
          <Th>Data Type</Th>
          <Th>Details</Th>
          <Th>Legal Basis</Th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <Td>Display name</Td>
          <Td>User-chosen name visible to group members</Td>
          <Td>Contract performance (Art. 6(1)(b))</Td>
        </tr>
        <tr>
          <Td>Email address</Td>
          <Td>Used for login, verification, and service notifications</Td>
          <Td>Contract performance (Art. 6(1)(b))</Td>
        </tr>
        <tr>
          <Td>Password</Td>
          <Td>
            Hashed and salted; we never store or access plaintext passwords
          </Td>
          <Td>Contract performance (Art. 6(1)(b))</Td>
        </tr>
      </tbody>
    </LegalTable>

    <LegalH3>3.2 Item and Loan Data</LegalH3>
    <LegalTable>
      <thead>
        <tr>
          <Th>Data Type</Th>
          <Th>Details</Th>
          <Th>Legal Basis</Th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <Td>Item listings</Td>
          <Td>
            Name, category, description, photos, circulation policy, availability
            status
          </Td>
          <Td>Contract performance (Art. 6(1)(b))</Td>
        </tr>
        <tr>
          <Td>Loan records</Td>
          <Td>
            Which user has borrowed which item, loan dates, due dates, return
            status
          </Td>
          <Td>Contract performance (Art. 6(1)(b))</Td>
        </tr>
        <tr>
          <Td>Group membership</Td>
          <Td>Which groups a user belongs to</Td>
          <Td>Contract performance (Art. 6(1)(b))</Td>
        </tr>
      </tbody>
    </LegalTable>

    <LegalH3>3.3 Technical and Usage Data</LegalH3>
    <LegalTable>
      <thead>
        <tr>
          <Th>Data Type</Th>
          <Th>Details</Th>
          <Th>Legal Basis</Th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <Td>IP address</Td>
          <Td>Collected automatically for security and abuse prevention</Td>
          <Td>Legitimate interests (Art. 6(1)(f))</Td>
        </tr>
        <tr>
          <Td>Device information</Td>
          <Td>
            Device type, OS, browser (for push notifications and error
            monitoring)
          </Td>
          <Td>Legitimate interests (Art. 6(1)(f))</Td>
        </tr>
        <tr>
          <Td>Usage analytics</Td>
          <Td>Pages visited, features used, session duration (via PostHog)</Td>
          <Td>Consent (Art. 6(1)(a)) via cookie preferences</Td>
        </tr>
        <tr>
          <Td>Error reports</Td>
          <Td>Application errors and crash data (via Sentry)</Td>
          <Td>Legitimate interests (Art. 6(1)(f))</Td>
        </tr>
        <tr>
          <Td>Push notification tokens</Td>
          <Td>Device tokens for sending push notifications (FCM/APNs)</Td>
          <Td>Consent (Art. 6(1)(a))</Td>
        </tr>
      </tbody>
    </LegalTable>

    <LegalH3>3.4 Data We Do Not Collect</LegalH3>
    <p>
      We do not collect: date of birth, postal address or postcode, precise
      location or GPS data, financial or payment card information (payments are
      handled entirely by Stripe; we receive only subscription status, not card
      details), phone number, or any biometric data.
    </p>

    {/* ── 4. How We Use Your Personal Data ─────────────────────────────── */}
    <LegalH2>4. How We Use Your Personal Data</LegalH2>
    <p>We use your personal data for the following purposes:</p>
    <ul style={{ marginTop: 8, marginBottom: 12, paddingLeft: 24 }}>
      <li>Account creation, authentication, and session management.</li>
      <li>
        Displaying your items and loan status to members of your groups.
      </li>
      <li>
        Sending essential service notifications (e.g. loan requests, return
        confirmations, overdue reminders) via email and push notifications.
      </li>
      <li>Monitoring application performance and fixing errors.</li>
      <li>
        Understanding how the Service is used so we can improve it (analytics,
        with consent only).
      </li>
      <li>Managing subscription status and enforcing tier limits.</li>
      <li>Detecting and preventing abuse or unauthorised access.</li>
      <li>
        Complying with legal obligations under UK GDPR and the Data Protection
        Act 2018.
      </li>
    </ul>
    <p>
      We do not use your data for automated decision-making, profiling,
      behavioural advertising, or any purpose unrelated to the operation of the
      Service.
    </p>

    {/* ── 5. How We Share Your Data ─────────────────────────────────────── */}
    <LegalH2>5. How We Share Your Data</LegalH2>

    <LegalH3>5.1 With Other Users</LegalH3>
    <p>
      Your display name, item listings, and loan activity are visible to members
      of groups you belong to. Your email address is never displayed to other
      users.
    </p>
    <p style={{ marginTop: 8 }}>
      Share Shack uses invite-only, private groups. Your data is never visible to
      users outside your groups, and there are no public listings or discovery
      features.
    </p>

    <LegalH3>5.2 With Service Providers</LegalH3>
    <LegalTable>
      <thead>
        <tr>
          <Th>Provider</Th>
          <Th>Purpose</Th>
          <Th>Data Shared</Th>
          <Th>Location</Th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <Td>Supabase</Td>
          <Td>Database, auth, file storage</Td>
          <Td>Account data, items, loans, photos</Td>
          <Td>UK</Td>
        </tr>
        <tr>
          <Td>Vercel</Td>
          <Td>Application hosting</Td>
          <Td>IP address, request metadata</Td>
          <Td>US</Td>
        </tr>
        <tr>
          <Td>Stripe</Td>
          <Td>Subscription payments</Td>
          <Td>Email, subscription status only</Td>
          <Td>US</Td>
        </tr>
        <tr>
          <Td>Resend</Td>
          <Td>Transactional email</Td>
          <Td>Email address, notification content</Td>
          <Td>US</Td>
        </tr>
        <tr>
          <Td>PostHog</Td>
          <Td>Product analytics (consent required)</Td>
          <Td>Anonymised usage events</Td>
          <Td>EU</Td>
        </tr>
        <tr>
          <Td>Sentry</Td>
          <Td>Error monitoring</Td>
          <Td>Error data, device info</Td>
          <Td>US</Td>
        </tr>
        <tr>
          <Td>Firebase (Google)</Td>
          <Td>Push notification delivery</Td>
          <Td>Device push tokens</Td>
          <Td>US</Td>
        </tr>
      </tbody>
    </LegalTable>
    <p>
      We do not sell, rent, or trade your personal data to any third party. We do
      not share data with advertisers or marketing companies.
    </p>

    <LegalH3>5.3 International Transfers</LegalH3>
    <p>
      Some of our service providers are based outside the UK. Where personal data
      is transferred internationally, we ensure appropriate safeguards are in
      place, including Standard Contractual Clauses (SCCs) and the UK
      International Data Transfer Agreement (IDTA), in accordance with Chapter V
      of the UK GDPR.
    </p>

    <LegalH3>5.4 Legal Disclosure</LegalH3>
    <p>
      We may disclose personal data where required by law, regulation, or court
      order, or where necessary to protect the rights, safety, or property of
      Share Shack, our users, or others.
    </p>

    {/* ── 6. Data Retention ────────────────────────────────────────────── */}
    <LegalH2>6. Data Retention</LegalH2>
    <LegalTable>
      <thead>
        <tr>
          <Th>Data Category</Th>
          <Th>Retention Period</Th>
          <Th>Basis</Th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <Td>Account data</Td>
          <Td>
            Duration of account. Soft-deleted for 30 days after deletion, then
            permanently erased.
          </Td>
          <Td>Contract performance</Td>
        </tr>
        <tr>
          <Td>Item listings</Td>
          <Td>Duration of listing. Removed when item or account is deleted.</Td>
          <Td>Contract performance</Td>
        </tr>
        <tr>
          <Td>Loan records</Td>
          <Td>
            Duration of account (loan history retained for accountability).
          </Td>
          <Td>Legitimate interests</Td>
        </tr>
        <tr>
          <Td>Usage analytics</Td>
          <Td>90 days identifiable, then aggregated and anonymised.</Td>
          <Td>Consent</Td>
        </tr>
        <tr>
          <Td>Error logs</Td>
          <Td>90 days.</Td>
          <Td>Legitimate interests</Td>
        </tr>
        <tr>
          <Td>Push notification tokens</Td>
          <Td>Deleted on sign-out or account deletion.</Td>
          <Td>Consent</Td>
        </tr>
      </tbody>
    </LegalTable>
    <p>
      When data is no longer required, it is securely deleted or irreversibly
      anonymised.
    </p>

    {/* ── 7. Your Rights Under UK GDPR ─────────────────────────────────── */}
    <LegalH2>7. Your Rights Under UK GDPR</LegalH2>
    <p>
      You have the following rights in relation to your personal data:
    </p>
    <ul style={{ marginTop: 8, marginBottom: 12, paddingLeft: 24 }}>
      <li>
        <strong>Right of access (Art. 15):</strong> You can export your data
        (items and loan history) at any time from the Account screen in the app.
      </li>
      <li>
        <strong>Right to rectification (Art. 16):</strong> You can update your
        display name and email address directly in the app.
      </li>
      <li>
        <strong>Right to erasure (Art. 17):</strong> You can delete your account
        at any time from the Account screen. This permanently removes your
        profile, items, group memberships, loan history, notifications, and
        subscription data after a 30-day soft-delete window.
      </li>
      <li>
        <strong>Right to restrict processing (Art. 18):</strong> Contact us to
        request restriction of processing.
      </li>
      <li>
        <strong>Right to data portability (Art. 20):</strong> The in-app data
        export provides your data in CSV and JSON formats.
      </li>
      <li>
        <strong>Right to object (Art. 21):</strong> You may object to processing
        based on legitimate interests by contacting us.
      </li>
      <li>
        <strong>Right to withdraw consent:</strong> You can withdraw analytics
        consent at any time via cookie preferences in your Account settings. You
        can disable push notifications via your device settings.
      </li>
    </ul>
    <p>
      To exercise any right not available through the app, contact us at{' '}
      <LegalLink href="mailto:support@shareshack.co.uk">
        support@shareshack.co.uk
      </LegalLink>
      . We will respond within one calendar month.
    </p>
    <p style={{ marginTop: 8 }}>
      If you are dissatisfied with our response, you have the right to lodge a
      complaint with the Information Commissioner's Office (ICO) at{' '}
      <LegalLink href="https://ico.org.uk">ico.org.uk</LegalLink> or by calling
      0303 123 1113.
    </p>

    {/* ── 8. Children's Data and the ICO Children's Code ───────────────── */}
    <LegalH2>8. Children's Data and the ICO Children's Code</LegalH2>
    <p>
      Share Shack is available to users aged 13 and over. We are committed to
      protecting the privacy of younger users.
    </p>

    <LegalH3>8.1 Age Requirement</LegalH3>
    <p>
      You must be at least 13 years old to create a Share Shack account. This
      aligns with the UK age of digital consent under Article 8 of the UK GDPR
      and Section 9 of the Data Protection Act 2018. We rely on the age
      restrictions enforced by the Apple App Store and Google Play Store, both of
      which require users to be at least 13 to hold an account.
    </p>
    <p style={{ marginTop: 8 }}>
      If we discover that a user is under 13, we will promptly delete the account
      and securely erase all associated personal data.
    </p>

    <LegalH3>8.2 Children's Code Compliance</LegalH3>
    <p>
      Share Shack is designed for use by families and trusted groups, meaning
      users aged 13–17 are a likely and expected part of our audience. We welcome
      this and have designed the Service with their privacy in mind from the
      outset, in conformance with the ICO's Age Appropriate Design Code. Our
      compliance measures include:
    </p>
    <ul style={{ marginTop: 8, marginBottom: 12, paddingLeft: 24 }}>
      <li>
        <strong>Best interests of the child (Standard 1):</strong> The Service is
        designed around private, trusted groups. There are no public profiles, no
        discovery features, and no contact between strangers. A young person
        using Share Shack is visible only to people already in their invited
        group — typically family and friends.
      </li>
      <li>
        <strong>Age-appropriate application (Standard 3):</strong> We rely on app
        store age gates (13+) as our age assurance mechanism. Given the low-risk
        nature of the data we process (item names, loan status) and the private,
        invite-only group model, this is proportionate to the risks involved.
      </li>
      <li>
        <strong>Transparency (Standard 4):</strong> This privacy policy is
        written in clear, plain language.
      </li>
      <li>
        <strong>Detrimental use of data (Standard 5):</strong> We do not use any
        user's data in ways that could be detrimental to their wellbeing. There
        are no addictive design patterns, no social comparison features, and no
        algorithmic content feeds.
      </li>
      <li>
        <strong>High privacy by default (Standard 6):</strong> All users receive
        high-privacy settings by default. Analytics are off until consent is
        given. Push notifications require explicit opt-in. User data is visible
        only within their own groups.
      </li>
      <li>
        <strong>Data minimisation (Standard 8):</strong> We collect only what is
        necessary: a display name, email, and the items/loans the user chooses to
        record. We do not collect age, location, phone number, or any data beyond
        what the Service requires.
      </li>
      <li>
        <strong>Data sharing (Standard 9):</strong> We do not share user data
        with advertisers, marketers, or any third party for commercial purposes.
      </li>
      <li>
        <strong>Geolocation (Standard 10):</strong> We do not collect, use, or
        store any location data.
      </li>
      <li>
        <strong>Profiling (Standard 12):</strong> We do not profile any users for
        behavioural advertising, content recommendation, or any other purpose.
      </li>
      <li>
        <strong>Nudge techniques (Standard 13):</strong> We do not use dark
        patterns or nudge techniques to encourage users to weaken privacy
        settings, share more data, or extend usage.
      </li>
      <li>
        <strong>Online tools (Standard 15):</strong> Users can exercise their
        data rights directly through the app (data export, account deletion,
        notification preferences, analytics consent).
      </li>
    </ul>

    <LegalH3>8.3 Reporting Concerns</LegalH3>
    <p>
      If you believe a user is under 13, or if you have any concerns about a
      young person's use of the Service, please contact us at{' '}
      <LegalLink href="mailto:support@shareshack.co.uk">
        support@shareshack.co.uk
      </LegalLink>
      .
    </p>

    {/* ── 9. Cookies and Tracking Technologies ─────────────────────────── */}
    <LegalH2>9. Cookies and Tracking Technologies</LegalH2>
    <p>
      Share Shack uses essential cookies required for the Service to function
      (authentication tokens and session management). These are strictly
      necessary and do not require consent under UK PECR.
    </p>
    <p style={{ marginTop: 8 }}>
      We use PostHog for product analytics. PostHog analytics cookies are only
      activated with your explicit consent via the cookie preferences in your
      Account settings. You can change your preference at any time. No analytics
      data is collected until you opt in.
    </p>
    <p style={{ marginTop: 8 }}>
      We do not use advertising cookies, tracking pixels, social media trackers,
      or any third-party marketing technologies.
    </p>

    {/* ── 10. Data Security ────────────────────────────────────────────── */}
    <LegalH2>10. Data Security</LegalH2>
    <p>
      We implement appropriate technical and organisational measures to protect
      your personal data, including:
    </p>
    <ul style={{ marginTop: 8, marginBottom: 12, paddingLeft: 24 }}>
      <li>Encryption in transit (TLS/HTTPS) for all data transmission.</li>
      <li>
        Hashed and salted password storage (we never have access to your
        password).
      </li>
      <li>
        Row-level security (RLS) policies on all database tables, ensuring users
        can only access data within their own groups.
      </li>
      <li>
        Rate limiting on account creation, item creation, and invite generation
        to prevent abuse.
      </li>
      <li>
        Regular security reviews and principle of least privilege for all system
        access.
      </li>
    </ul>
    <p>
      No system is completely secure. If a data breach occurs that poses a risk
      to your rights and freedoms, we will notify you and the ICO in accordance
      with Articles 33 and 34 of the UK GDPR.
    </p>

    {/* ── 11. Changes to This Privacy Policy ───────────────────────────── */}
    <LegalH2>11. Changes to This Privacy Policy</LegalH2>
    <p>
      We may update this Privacy Policy from time to time. Where changes are
      significant, we will notify you via email or in-app notification before the
      changes take effect. The "Last Updated" date at the top of this document
      will always reflect the most recent revision.
    </p>
    <p style={{ marginTop: 8 }}>
      Continued use of the Service after notification constitutes acceptance of
      the updated policy. If you do not agree, you may delete your account at any
      time.
    </p>

    {/* ── 12. Contact Us ───────────────────────────────────────────────── */}
    <LegalH2>12. Contact Us</LegalH2>
    <p>
      For any questions, concerns, or requests relating to this Privacy Policy or
      your personal data:
    </p>
    <p style={{ marginTop: 8 }}>
      Email:{' '}
      <LegalLink href="mailto:support@shareshack.co.uk">
        support@shareshack.co.uk
      </LegalLink>
      <br />
      Share Shack
    </p>
    <p style={{ marginTop: 8 }}>
      Information Commissioner's Office (ICO):{' '}
      <LegalLink href="https://ico.org.uk">ico.org.uk</LegalLink> | 0303 123
      1113
    </p>
  </LegalPageLayout>
);

export default PrivacyPolicy;
